import { useEffect, useMemo, useState } from "react";
import type { MediaItem } from "@/types/media";
import { applyFilters, getFilterMeta, type SortKey } from "@/lib/mediaQuery";
import MediaCard from "./MediaCard";
import Pagination from "./Pagination";
import { CardSkeleton } from "@/components/base/Skeleton";

const PAGE_SIZE = 8;
const REGIONS = ["全部", "国产", "海外"];
const RATINGS = [
  { label: "全部", value: 0 },
  { label: "8 分以上", value: 8 },
  { label: "8.5 分以上", value: 8.5 },
  { label: "9 分以上", value: 9 },
];
const SORTS: { key: SortKey; label: string }[] = [
  { key: "updated", label: "按更新时间" },
  { key: "views", label: "按播放量" },
  { key: "rating", label: "按评分" },
];

interface MediaBrowserProps {
  items: MediaItem[];
}

export default function MediaBrowser({ items }: MediaBrowserProps) {
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [minRating, setMinRating] = useState(0);
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const meta = useMemo(() => getFilterMeta(items), [items]);

  const filtered = useMemo(
    () => applyFilters(items, { region, year, minRating, genre, sort }),
    [items, region, year, minRating, genre, sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [region, year, minRating, genre, sort, items]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [items, region, year, minRating, genre, sort]);

  const reset = () => {
    setRegion("全部");
    setYear("全部");
    setMinRating(0);
    setGenre("全部");
    setSort("updated");
  };

  const chip = (active: boolean) =>
    `px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
      active
        ? "bg-primary-500 text-background-50"
        : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
    }`;

  const filterPanel = (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-sm font-bold text-foreground-900 mb-3">地区</h3>
        <div className="flex flex-wrap gap-2">
          {REGIONS.map((r) => (
            <button key={r} type="button" onClick={() => setRegion(r)} className={chip(region === r)}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-bold text-foreground-900 mb-3">年份</h3>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="全部">全部年份</option>
          {meta.years.map((y) => (
            <option key={y} value={y}>
              {y} 年
            </option>
          ))}
        </select>
      </div>

      <div>
        <h3 className="font-heading text-sm font-bold text-foreground-900 mb-3">评分区间</h3>
        <div className="flex flex-wrap gap-2">
          {RATINGS.map((r) => (
            <button key={r.label} type="button" onClick={() => setMinRating(r.value)} className={chip(minRating === r.value)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-bold text-foreground-900 mb-3">剧情标签</h3>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setGenre("全部")} className={chip(genre === "全部")}>
            全部
          </button>
          {meta.genres.map((g) => (
            <button key={g} type="button" onClick={() => setGenre(g)} className={chip(genre === g)}>
              {g}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={reset}
        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line"></i>
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
      <aside className="hidden lg:block">
        <div className="bg-background-50 border border-background-200 rounded-lg p-5 sticky top-24">{filterPanel}</div>
      </aside>

      <div>
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="lg:hidden inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-100 text-sm text-foreground-800 cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
            <p className="text-sm text-foreground-500">
              共 <span className="text-primary-600 font-medium">{filtered.length}</span> 部内容
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 hidden sm:block">排序</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="bg-background-50 border border-background-300 rounded-md px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {mobileFilterOpen && (
          <div className="lg:hidden bg-background-50 border border-background-200 rounded-lg p-5 mb-5 animate-fade-in">
            {filterPanel}
          </div>
        )}

        {loading ? (
          <CardSkeleton count={PAGE_SIZE} />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-20 bg-background-50 border border-background-200 rounded-lg">
            <span className="w-16 h-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">暂无匹配的影视内容</h3>
            <p className="text-sm text-foreground-500 max-w-sm mb-5">
              当前筛选条件下没有找到结果，你可以尝试放宽筛选条件，或重置后重新浏览。
            </p>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {paged.map((item, i) => (
                <MediaCard key={item.id} item={item} index={i} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}